import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Icon,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Image,
  Spinner,
  useDisclosure,
  useToast,
  HStack,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import Calendar from "react-calendar";
import {
  IoIosArrowBack,
  IoIosRadioButtonOn,
  IoIosRadioButtonOff,
  IoIosArrowForward,
} from "react-icons/io";
// import { HiMail } from "react-icons/hi";
import { IoChevronDown, IoCalendarOutline, IoGift } from "react-icons/io5";
import { RiCheckboxBlankLine, RiCheckboxFill } from "react-icons/ri";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

import { PaymentDisclosure } from "../features/payment/PaymentDisclosure";
import {
  getTeamMembers,
  fetchDiscountCoupon,
  createBooking,
} from "../services/booking";
import { fetchLeave, getAvailableSlots } from "../services/profile";
import LogoText from "../assets/images/logoText.png";
import {
  generateTimeSlots,
  formatDate,
  getUnavailableDates,
  getNextUnavailableDates,
} from "../core/utils";

// const serviceTypes = [
//   {
//     id: 1,
//     value: "Mobile",
//     slug: "mobile",
//     additionalNote: "(Notice: additional travel fee may apply)",
//   },
//   { id: 2, value: "Salon/Studio Based", slug: "salon", additionalNote: "" },
// ];

const depositTypes = [
  { id: 1, value: "Pay total amount", slug: "pay_online" },
  { id: 2, value: "Pay Deposit 50%", slug: "pay_at_venue" },
  { id: 3, value: "Pay Deposit 25%", slug: "pay_at_venue_25" },
];

export const Schedule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, name } = location.state || {};

  // If no state is present, go back
  useEffect(() => {
    if (!location.state) {
      navigate(-1);
    }
  }, [location.state, navigate]);

  const dateDisclosure = useDisclosure();
  const paymentDisclosure = useDisclosure();
  const toast = useToast();

  const stripe: any = useStripe();
  const elements = useElements();

  const [date, setDate] = useState<any>();
  const [depositType, setDepositType] = useState<string>("");
  const [acceptTAndC, setAcceptTAndC] = useState<boolean>(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [discountCode, setDiscountCode] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [additionalInformation, setAdditionalInformation] = useState<any>({
    address: "",
    parking: "",
    pets: "",
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [expandedTAndC, setExpandedTAndC] = useState<boolean>(false);

  const [discount, setDiscount] = useState<any>();
  const [team, setTeam] = useState<any[]>([]);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [customSlots, setCustomSlots] = useState<any>();
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);

  // Every cart item belongs to the same business (enforced when adding to the
  // cart), so the first item is authoritative.
  const businessId = cart[0]?.business_id;

  const serviceDuration = cart.reduce(
    (accumulator: any, currentValue: any) =>
      accumulator + currentValue.finalDuration,
    0,
  );

  useEffect(() => {
    const fetchSchedule = async () => {
      setDisabledDates([]);
      setSchedule([]);
      setDate(undefined);
      setStartTime("");

      const leaveResponse = await fetchLeave({
        business_id: businessId,
        team_member_id: selectedTeamMember,
        timezone: "Europe/London",
      });

      // Define leave dates
      const leaveDates = leaveResponse.map(
        (leave: any) =>
          new Date(
            leave.leave_date.split("-")[2],
            leave.leave_date.split("-")[1] - 1,
            leave.leave_date.split("-")[0],
          ),
      );

      const availableSlotsResponse = await getAvailableSlots({
        business_id: businessId,
        team_member_id: selectedTeamMember,
        fetchMultiple: true,
        start_date: new Date().toISOString().split("T")[0],
        end_date: "2030-01-01",
        timezone: "Europe/London",
      });

      setCustomSlots(availableSlotsResponse);

      // Track missing days of week (1-7 where 1=Sunday)
      let missingDows: number[] = [];
      let missingDayDates: string[] = [];

      if (Array.isArray(availableSlotsResponse?.schedule)) {
        const scheduleArray = availableSlotsResponse?.schedule;
        const present = new Set<number>(
          scheduleArray.map((d: { day_of_week: number }) => d.day_of_week),
        );
        if (present.size !== 7) {
          const all = [1, 2, 3, 4, 5, 6, 7];
          missingDows = all.filter((d) => !present.has(d));
        }
      }

      // If schedule is an empty array, disable all dates in the calendar
      if (
        Array.isArray(availableSlotsResponse?.schedule) &&
        availableSlotsResponse?.schedule.length === 0
      ) {
        // Generate all dates from today to 2030-01-01 (end_date from API call)
        const allDates: Date[] = [];
        const startDate = new Date();
        const endDate = new Date("2030-01-01");

        let currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          allDates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }

        setDisabledDates(allDates);
        return; // Exit early as all dates are disabled
      }

      // Calculate days until end of month, 3 months from now
      const today = new Date();
      const futureDate = new Date(
        today.getFullYear(),
        today.getMonth() + 3 + 1,
        0,
      );
      const daysInFuture = Math.ceil(
        (futureDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );

      // If any weekdays are missing from schedule, generate all dates for those weekdays from today to daysInFuture
      if (missingDows.length > 0) {
        const startDate = new Date(today);
        const dates: any[] = [];
        for (let i = 0; i <= daysInFuture; i++) {
          const d = new Date(startDate);
          d.setDate(startDate.getDate() + i);
          // Convert JS getDay() [0..6, Sunday=0] => [1..7, Sunday=1]
          const dow = d.getDay() === 0 ? 1 : d.getDay() + 1;
          if (missingDows.includes(dow)) {
            dates.push(d);
          }
        }
        missingDayDates = dates;
      }

      let unavailableDatesFromSchedule = getNextUnavailableDates(
        availableSlotsResponse?.schedule?.filter(({ schedules }: any) =>
          schedules?.every((schedule: any) => !schedule.isAvailable),
        ),
        daysInFuture,
      );

      // Filtering out dates which are in custom schedule
      let customDatesSet = new Set(
        availableSlotsResponse.custom_schedule.map((schedule: any) =>
          new Date(schedule.filter_date).toDateString(),
        ),
      );
      unavailableDatesFromSchedule = unavailableDatesFromSchedule.filter(
        (date) => !customDatesSet.has(date.toDateString()),
      );

      // Process unavailable dates from custom availability
      const unavailableDates = getUnavailableDates(
        availableSlotsResponse.custom_schedule,
        serviceDuration,
      );

      // Convert unavailable dates to Date objects
      const unavailableDatesFromCustomSchedule = unavailableDates.map(
        (dateStr: any) => {
          const [year, month, day] = dateStr.split("-").map(Number);
          return new Date(year, month - 1, day);
        },
      );

      const combined = [
        ...leaveDates,
        ...unavailableDatesFromCustomSchedule,
        ...unavailableDatesFromSchedule,
        ...missingDayDates,
      ];

      const uniqueCombined = Array.from(new Set(combined));
      setDisabledDates(uniqueCombined);
    };

    selectedTeamMember && fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamMember]);

  useEffect(() => {
    setStartTime("");

    const filteredSchedule = customSlots?.schedule
      .find(
        (day: { day_of_week: number }) =>
          day.day_of_week === new Date(date).getDay() + 1,
      )
      ?.schedules?.filter((availability: any) => availability.isAvailable);

    let slots: string[] = [];

    if (filteredSchedule) {
      filteredSchedule.forEach((_schedule: any) => {
        const timeSlots = generateTimeSlots(
          _schedule?.start_time_lcl,
          _schedule?.end_time_lcl,
          30,
          serviceDuration,
        );
        slots = [...slots, ...timeSlots];
      });
    }

    // Filter out past slots and apply 5-hour buffer if selected day is today
    if (date) {
      const selectedDate = new Date(date);
      const currentDay = new Date(); // Current local time
      const bufferHours = 5; // 5-hour buffer

      if (
        selectedDate.getFullYear() === currentDay.getFullYear() &&
        selectedDate.getMonth() === currentDay.getMonth() &&
        selectedDate.getDate() === currentDay.getDate()
      ) {
        // Add buffer to current time
        const bufferTime = new Date(currentDay);
        bufferTime.setHours(bufferTime.getHours() + bufferHours);

        // Filter slots to only show those after the buffer time
        slots = slots.filter((slot) => {
          // Create a new date object for the slot time
          const [slotHour, slotMinute] = slot.split(":").map(Number);
          const slotDate = new Date(selectedDate);
          slotDate.setHours(slotHour, slotMinute, 0, 0);

          const isAvailable = slotDate.getTime() > bufferTime.getTime();

          return isAvailable;
        });
      }
    }

    setSchedule(slots);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    getTeamMembers(businessId).then((res) => setTeam(res));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (date) {
      const customTodaySlots = customSlots.custom_schedule.filter(
        (slot: { filter_date: string }) =>
          slot.filter_date === formatDate(date),
      );

      if (customTodaySlots.length > 0) {
        const allTimeSlots = customTodaySlots.flatMap(
          ({ start_time, end_time }: any) => {
            // Convert ISO date to "HH:mm" format
            const formattedStart = new Date(start_time)
              .toISOString()
              .substring(11, 16);
            const formattedEnd = new Date(end_time)
              .toISOString()
              .substring(11, 16);

            return generateTimeSlots(
              formattedStart,
              formattedEnd,
              30,
              serviceDuration,
            );
          },
        );

        setSchedule(Array.from(new Set(allTimeSlots)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handlePayPress = async () => {
    setLoading(true);

    if (!stripe || !elements) {
      console.error("Stripe has not loaded yet.");
      return;
    }

    // Create a payment method using card details
    const cardElement = elements.getElement(CardElement);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (error) {
      console.error(error);
    } else {
      const amount = cart.reduce(
        (accumulator: any, currentValue: { finalPrice: any }) =>
          accumulator + currentValue.finalPrice,
        0,
      );

      let discountedVal = 1;
      if (depositType === "pay_online") discountedVal = 1;
      else if (depositType === "pay_at_venue") discountedVal = 0.5;
      else if (depositType === "pay_at_venue_25") discountedVal = 0.25;

      const dayDate = new Date(date);
      const timeDate = new Date(`1970-01-01T${startTime}:00`);

      const combinedDate = new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate(),
        timeDate.getUTCHours(),
        timeDate.getUTCMinutes(),
        timeDate.getUTCSeconds(),
        timeDate.getUTCMilliseconds(),
      );

      // Services run back-to-back: each one starts when the previous ends.
      let slotCursor = combinedDate.getTime();

      const body = {
        business_id: businessId,
        booking_data: cart.map((item: any) => {
          const itemDuration = Number(
            item.finalDuration ?? item.service.duration_minute,
          );
          const slotStart = slotCursor;
          const slotEnd = slotStart + itemDuration * 60 * 1000;
          slotCursor = slotEnd;

          return {
            service_id: item.service._id,
            team_member_id: selectedTeamMember,
            slot_start_time: new Date(slotStart).toISOString(),
            slot_end_time: new Date(slotEnd).toISOString(),
            servicePrice: item.finalPrice,
            serviceDuration: itemDuration,
            // Only the groups actually chosen: the server ranks every key it
            // receives, and an empty value ranks as -1 rather than as "not set".
            serviceExtras: {
              ...(item.selectedLength ? { length: item.selectedLength } : {}),
              ...(item.selectedSize ? { size: item.selectedSize } : {}),
            },
          };
        }),
        gross_amount: amount,
        discount_amount: discount?.discount_amount || 0,
        net_total: amount,
        paid_amount: amount * discountedVal,
        discount_coupon_id: discount?.data?._id || "",
        // Only sent when the customer actually entered a code.
        ...(discountCode ? { discount_code: discountCode } : {}),
        isAgreeOnTerms: String(acceptTAndC),
        paymentMethod: depositType,
        stripePaymentMethodId: paymentMethod.id,
        name,
        additionalNotes: notes,
        additionalInformation,
        timezone: "Europe/London",
        platform: "booking-ui",
      };

      const res = await createBooking(body);

      if (res?.status === 200) {
        toast({
          title: "Success",
          description: res.message,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });

        paymentDisclosure.onClose();

        const _selectedTeamMember: any = team.find(
          (user: { _id: any }) => user._id === selectedTeamMember,
        );
        navigate("/success", {
          state: {
            firstName: _selectedTeamMember.first_name,
            lastName: _selectedTeamMember.last_name,
            serviceName: cart[0].service.name,
            amount: amount * discountedVal,
            date,
            startTime,
          },
        });
      } else {
        toast({
          title: "An error occcured",
          description: res?.message,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    }

    setLoading(false);
  };

  const applyDiscountCoupon = async () => {
    setLoading(true);

    if (discountCode) {
      const res = await fetchDiscountCoupon({
        coupon_code: discountCode,
        gross_amount: cart.reduce(
          (accumulator: any, currentValue: { service: { price: any } }) =>
            accumulator + currentValue.service.price,
          0,
        ),
        business_id: businessId,
      });

      if (res.status === 200) {
        setDiscount(res);

        toast({
          title: "Success",
          description: res.message,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } else {
        toast({
          title: "An error occcured",
          description: res.message,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    }

    setLoading(false);
  };

  // Function to check if a date is disabled by comparing year, month, and day
  const isDateDisabled = (date: Date) => {
    return disabledDates.some((disabledDate) => {
      return (
        disabledDate.getFullYear() === date.getFullYear() &&
        disabledDate.getMonth() === date.getMonth() &&
        disabledDate.getDate() === date.getDate()
      );
    });
  };

  const yesNoOptions = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  return (
    <Box h="100vh" overflowY={"hidden"}>
      <Flex
        boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
        h="72px"
        px="10%"
        align={"center"}
        display={{ base: "none", md: "flex" }}
      >
        <Icon fontSize={24} color={"#19202D"} onClick={() => navigate(-1)}>
          <IoIosArrowBack />
        </Icon>
      </Flex>

      <Flex
        justify={"space-between"}
        align={"center"}
        bg={"linear-gradient(to bottom right, #FFF7F3, #D8F1F2)"}
        borderBottomLeftRadius={24}
        borderBottomRightRadius={24}
        px={"20px"}
        pb={"20px"}
        pt={"54px"}
        display={{ base: "flex", md: "none" }}
      >
        <Icon fontSize={24} color={"#19202D"} onClick={() => navigate(-1)}>
          <IoIosArrowBack />
        </Icon>

        <Heading fontSize={"20px"} textAlign={"center"} color={"#19202D"}>
          Schedule Appointment
        </Heading>

        <Box w={"24px"} />
      </Flex>

      <Flex
        flexDir={"row"}
        gap={"56px"}
        align={"flex-start"}
        px={{ base: "20px", md: "10%" }}
        pt="24px"
        h={`calc(100vh - 72px)`}
        overflowY={"scroll"}
      >
        <Box w={{ base: "100%", md: "60%" }}>
          <Flex
            gap={"6px"}
            align={"center"}
            mb="32px"
            display={{ base: "none", md: "flex" }}
          >
            <Text fontSize={"16px"} color={"#4F4F51"} lineHeight={"24px"}>
              Home
            </Text>

            <Icon fontSize={"20px"} color={"#19202D"}>
              <IoIosArrowForward />
            </Icon>

            <Text fontSize={"16px"} color={"#4F4F51"} lineHeight={"24px"}>
              Services
            </Text>

            <Icon fontSize={"20px"} color={"#19202D"}>
              <IoIosArrowForward />
            </Icon>

            <Text fontSize={"16px"} color={"#FF762C"} lineHeight={"24px"}>
              Confirm
            </Text>
          </Flex>

          <Box mb={{ base: "20px", md: "24px" }}>
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Beauty Professional
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <Select
                placeholder="Select team member"
                border={0}
                icon={
                  <Icon fontSize={24} color={"#19202D"} mr="16px">
                    <IoChevronDown />
                  </Icon>
                }
                _focusVisible={{ borderColor: "none" }}
                value={selectedTeamMember}
                onChange={(e) => setSelectedTeamMember(e.target.value)}
              >
                {team?.length !== 0 &&
                  team?.map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.first_name} {member.last_name}
                    </option>
                  ))}
              </Select>
            </InputGroup>
          </Box>

          {/* <Box mb={{ base: "20px", md: "24px" }}>
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Service Type
            </Heading>

            <Text
              fontSize={"14px"}
              color={"#4F4F51"}
              pb={{ base: "0", md: "16px" }}
            >
              This professional offers services of these types. Please select
              your desired option and provide your address if necessary
            </Text>

            {serviceTypes.map((_serviceType) => (
              <Flex
                gap="8px"
                mt="16px"
                key={_serviceType.id}
                cursor={"pointer"}
                onClick={() => setServiceType(_serviceType.slug)}
              >
                <Icon fontSize={"24px"} color={"#FF8E3C"}>
                  {serviceType === _serviceType.slug ? (
                    <IoIosRadioButtonOn />
                  ) : (
                    <IoIosRadioButtonOff />
                  )}
                </Icon>

                <Flex align={"center"} gap={"6px"}>
                  <Text fontSize={"16px"} color={"#4F4F51"} lineHeight={"24px"}>
                    {_serviceType.value}
                  </Text>

                  <Text fontSize={"14px"} color={"#4F4F51"} lineHeight={"21px"}>
                    {_serviceType.additionalNote}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Box> */}

          {/* <Box mb={{ base: "20px", md: "24px" }}>
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Email
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={24} color={"#9BA3A3"}>
                  <HiMail />
                </Icon>
              </InputLeftElement>

              <Input
                placeholder="Enter Your Email"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                _focusVisible={{ borderColor: "none" }}
                type="email"
              />
            </InputGroup>
          </Box> */}

          <Flex
            mb={{ base: "20px", md: "24px" }}
            gap={"20px"}
            flexDir={{ base: "column", md: "row" }}
          >
            <Box flex={1}>
              <Heading
                fontSize={"16px"}
                color={"#19202D"}
                mb="8px"
                fontFamily={"RedHatDisplay-Bold"}
              >
                Appointment Date
              </Heading>

              <Flex
                borderRadius={"40px"}
                h="56px"
                alignItems={"center"}
                justify={"space-between"}
                px="16px"
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                onClick={() =>
                  selectedTeamMember ? dateDisclosure.onOpen() : () => {}
                }
                cursor={selectedTeamMember ? "pointer" : "not-allowed"}
              >
                <Text
                  color={selectedTeamMember ? "black" : "#9BA3A3"}
                  fontSize={"16px"}
                >
                  {date ? date.toLocaleDateString() : "Select Date"}
                </Text>

                <Icon fontSize={24} color={"#9BA3A3"}>
                  <IoCalendarOutline />
                </Icon>
              </Flex>
            </Box>

            <Box flex={1}>
              <Heading
                fontSize={"16px"}
                color={"#19202D"}
                mb="8px"
                fontFamily={"RedHatDisplay-Bold"}
              >
                Appointment Time
              </Heading>

              <InputGroup
                borderRadius={"40px"}
                h="56px"
                alignItems={"center"}
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              >
                <Select
                  placeholder="Select Time"
                  border={0}
                  icon={
                    <Icon fontSize={24} color={"#19202D"} mr="16px">
                      <IoChevronDown />
                    </Icon>
                  }
                  _focusVisible={{ borderColor: "none" }}
                  value={startTime}
                  onChange={(e) =>
                    selectedTeamMember ? setStartTime(e.target.value) : () => {}
                  }
                  disabled={!schedule?.length}
                >
                  {schedule?.length !== 0 &&
                    schedule?.map((day: any, index: any) => (
                      <option key={index} value={day}>
                        {day}
                      </option>
                    ))}
                </Select>
              </InputGroup>
            </Box>
          </Flex>

          <Box display={{ base: "none", md: "block" }}>
            {/* <Box mb="24px">
              <Heading
                fontSize={"16px"}
                color={"#19202D"}
                mb="8px"
                fontFamily={"RedHatDisplay-Bold"}
              >
                Where did you hear about me?
              </Heading>

              <InputGroup
                borderRadius={"40px"}
                h="56px"
                alignItems={"center"}
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              >
                <Select
                  placeholder="Please Select"
                  border={0}
                  icon={
                    <Icon fontSize={24} color={"#19202D"} mr="16px">
                      <IoChevronDown />
                    </Icon>
                  }
                  _focusVisible={{ borderColor: "none" }}
                >
                  <option value="option1">Option 1</option>
                  <option value="option2">Option 2</option>
                  <option value="option3">Option 3</option>
                </Select>
              </InputGroup>
            </Box>*/}

            <Box mb="24px">
              <Heading
                fontSize={"16px"}
                color={"#19202D"}
                mb="8px"
                fontFamily={"RedHatDisplay-Bold"}
              >
                Additional Information
              </Heading>

              <Text fontSize={"14px"} color={"#4F4F51"}>
                For mobile services, we require additional information about
                your location.
              </Text>

              <InputGroup
                borderRadius={"40px"}
                h="56px"
                alignItems={"center"}
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              >
                <Input
                  placeholder="Your Address (for mobile services)"
                  borderWidth={0}
                  fontSize={"16px"}
                  _focusVisible={{ borderColor: "none" }}
                  value={additionalInformation?.address}
                  onChange={(e) =>
                    setAdditionalInformation({
                      ...additionalInformation,
                      address: e.target.value,
                    })
                  }
                  type="address"
                />
              </InputGroup>

              <InputGroup
                borderRadius={"40px"}
                h="56px"
                alignItems={"center"}
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                my="8px"
                // color={"#9BA3A3"}
              >
                <Select
                  placeholder="Is there parking available?"
                  fontSize={"16px"}
                  border={0}
                  icon={
                    <Icon fontSize={24} color={"#19202D"} mr="16px">
                      <IoChevronDown />
                    </Icon>
                  }
                  _focusVisible={{ borderColor: "none" }}
                  value={additionalInformation?.parking}
                  onChange={(e) =>
                    setAdditionalInformation({
                      ...additionalInformation,
                      parking: e.target.value,
                    })
                  }
                >
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </InputGroup>

              <InputGroup
                borderRadius={"40px"}
                h="56px"
                alignItems={"center"}
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                my="8px"
              >
                <Select
                  placeholder="Do you have any pets?"
                  border={0}
                  fontSize={"16px"}
                  icon={
                    <Icon fontSize={24} color={"#19202D"} mr="16px">
                      <IoChevronDown />
                    </Icon>
                  }
                  _focusVisible={{ borderColor: "none" }}
                  value={additionalInformation?.pets}
                  onChange={(e) =>
                    setAdditionalInformation({
                      ...additionalInformation,
                      pets: e.target.value,
                    })
                  }
                >
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </InputGroup>
            </Box>

            <Box mb="24px">
              <Heading
                fontSize={"16px"}
                color={"#19202D"}
                mb="8px"
                fontFamily={"RedHatDisplay-Bold"}
              >
                Any additional notes for your Book-d Pro?
              </Heading>

              <InputGroup
                borderRadius={"40px"}
                h="56px"
                alignItems={"center"}
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              >
                <Input
                  placeholder="Notes"
                  borderWidth={0}
                  fontSize={"16px"}
                  _focusVisible={{ borderColor: "none" }}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </InputGroup>
            </Box>

            <Box mt="24px">
              <Heading fontSize={"18px"} color={"#19202D"}>
                Terms & Conditions
              </Heading>

              <Text
                fontSize={"14px"}
                mt="7px"
                color={"#4F4F51"}
                noOfLines={expandedTAndC ? undefined : 3}
              >
                By booking an appointment with me, you agree to the following
                Terms and Conditions: Appointment Cancellations: Please provide
                at least 24 hours' notice for any cancellations or rescheduling.
                Late Arrivals: If you arrive late, your appointment may need to
                be shortened to accommodate the next client. Payment: Payment is
                due at the time of service, either by cash or card. No-Show
                Policy: Failure to show up for your appointment without prior
                notice may result in a cancellation fee. Satisfaction Guarantee:
                If you're not satisfied with your service, please let me know
                immediately so I can make it right. Thank you for your
                understanding and cooperation. I look forward to providing you
                with excellent service!
              </Text>

              {
                <Text
                  fontSize={"14px"}
                  mt="7px"
                  color={"#4F4F51"}
                  cursor={"pointer"}
                  onClick={() => setExpandedTAndC((prev) => !prev)}
                  textDecoration={"underline"}
                  fontWeight={600}
                >
                  Read {!expandedTAndC ? "More" : "Less"}...
                </Text>
              }
            </Box>

            <Flex mt="24px" gap={"8px"}>
              <Icon
                fontSize={"24px"}
                color={"#FF8E3C"}
                onClick={() => setAcceptTAndC((prev) => !prev)}
              >
                {acceptTAndC ? <RiCheckboxFill /> : <RiCheckboxBlankLine />}
              </Icon>

              <Text fontSize={"14px"} color={"#4F4F51"}>
                By checking this, I confirm that I have read and agree to the
                Terms and Conditions outlined above and Book-d Terms of Service.
              </Text>
            </Flex>

            <Box
              h={"1px"}
              w={"100%"}
              borderRadius={"10px"}
              bg="#E9EDEF"
              mt="20px"
              mb="90px"
            />
          </Box>

          <Button
            bg={"#FF8E3C"}
            _hover={{ bg: "#FF8E3C" }}
            mt="24px"
            borderRadius={"30px"}
            color={"white"}
            w="100%"
            h="58px"
            fontSize={"17px"}
            fontFamily={"RedHatDisplay-Bold"}
            display={{ base: "block", md: "none" }}
            disabled={!selectedTeamMember || !date || !startTime}
            onClick={() =>
              navigate("/finalize-booking", {
                state: {
                  cart,
                  name,
                  date,
                  startTime,
                  selectedTeamMember,
                  firstName: team.find(
                    (user: { _id: any }) => user._id === selectedTeamMember,
                  ).first_name,
                  lastName: team.find(
                    (user: { _id: any }) => user._id === selectedTeamMember,
                  ).last_name,
                },
              })
            }
          >
            Next
          </Button>
        </Box>

        <Box
          w={"40%"}
          display={{ base: "none", md: "block" }}
          bg={
            "linear-gradient(#FFFFFF, #FFFFFF, #FFFFFF, #FFFFFF, #FFF7F3, #D8F1F2)"
          }
          boxShadow="0px 1px 3px rgba(0, 0, 0, 0.2)"
          p={"20px"}
          borderRadius={"20px"}
          pos={"sticky"}
          top={0}
          h={"fit-content"}
        >
          <Heading fontSize={"22px"} color={"#19202D"}>
            Book Services
          </Heading>

          {cart.map((item: any, index: number) => (
            <Flex
              key={index}
              boxShadow="0px 0.6px 3px rgba(0, 0, 0, 0.2)"
              mt="20px"
              p={"12px"}
              borderRadius={"20px"}
              justify={"space-between"}
              align={"flex-start"}
              gap={"24px"}
            >
              <Box>
                <Text fontWeight={700} fontSize={"16px"} color={"#4F4F51"}>
                  {item.service.name}
                </Text>

                <Text fontSize={"14px"} color={"#4F4F51"} mt="4px">
                  {item.service.description}
                </Text>
              </Box>

              <HStack>
                <Text fontWeight={700} fontSize={"16px"} color={"#267684"}>
                  £
                </Text>
                <Text fontWeight={700} fontSize={"16px"} color={"#267684"}>
                  {item.finalPrice}
                </Text>
              </HStack>
            </Flex>
          ))}

          <Box
            h={"1px"}
            w={"100%"}
            borderRadius={"10px"}
            bg="#E9EDEF"
            my="20px"
          />

          <Flex my="12px" justify={"space-between"} align={"center"}>
            <Text fontWeight={700} fontSize={"24px"} color={"#4F4F51"}>
              Total
            </Text>

            <Text fontWeight={700} fontSize={"24px"} color={"#267684"}>
              £{" "}
              {cart.reduce(
                (accumulator: any, currentValue: { finalPrice: any }) =>
                  accumulator + currentValue.finalPrice,
                0,
              ) - (discount?.discount_amount || 0)}
            </Text>
          </Flex>

          <Box mt="20px">
            <Heading fontSize={"18px"} color={"#19202D"} mb="8px">
              Payment Methods
            </Heading>

            {depositTypes.map((_depositType) => (
              <Flex
                gap="8px"
                mt="16px"
                key={_depositType.id}
                cursor={"pointer"}
                onClick={() => setDepositType(_depositType.slug)}
              >
                <Icon fontSize={"24px"} color={"#FF8E3C"}>
                  {depositType === _depositType.slug ? (
                    <IoIosRadioButtonOn />
                  ) : (
                    <IoIosRadioButtonOff />
                  )}
                </Icon>

                <Flex align={"center"} gap={"6px"}>
                  <Text fontSize={"16px"} color={"#4F4F51"} lineHeight={"24px"}>
                    {_depositType.value}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Box>

          <Box mt="32px">
            <Flex justify={"space-between"} align={"center"} mb="8px">
              <Heading
                fontSize={"16px"}
                color={"#19202D"}
                fontFamily={"RedHatDisplay-Bold"}
              >
                Discount Code
              </Heading>

              {loading ? (
                <Spinner color="#FF8E3C" />
              ) : (
                <Text
                  fontSize={"14px"}
                  fontWeight={500}
                  color={"#267684"}
                  cursor={"pointer"}
                  onClick={applyDiscountCoupon}
                >
                  Apply
                </Text>
              )}
            </Flex>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={24} color={"#9BA3A3"}>
                  <IoGift />
                </Icon>
              </InputLeftElement>

              <Input
                placeholder="** ** **"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                _focusVisible={{ borderColor: "none" }}
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
              />
            </InputGroup>
          </Box>

          <Button
            bg={"#FF8E3C"}
            _hover={{ bg: "#FF8E3C" }}
            mt="24px"
            borderRadius={"30px"}
            color={"white"}
            w="100%"
            h="58px"
            fontSize={"17px"}
            fontFamily={"RedHatDisplay-Bold"}
            onClick={() => paymentDisclosure.onOpen()}
            disabled={
              !depositType ||
              !selectedTeamMember ||
              !date ||
              !startTime ||
              !acceptTAndC
            }
          >
            Pay and Book
          </Button>

          <Text
            fontSize={"14px"}
            color={"#4F4F51"}
            textAlign={"center"}
            mt="30px"
          >
            Scheduling made simple by
          </Text>

          <Image
            src={LogoText}
            h="30px"
            objectFit={"contain"}
            justifySelf="center"
            my="10px"
          />
        </Box>
      </Flex>

      <Drawer
        placement={"bottom"}
        onClose={dateDisclosure.onClose}
        isOpen={dateDisclosure.isOpen}
      >
        <DrawerOverlay />

        <DrawerContent
          borderTopLeftRadius={"20px"}
          borderTopRightRadius={"20px"}
        >
          <DrawerCloseButton />

          <DrawerBody pt="50px" alignSelf={"center"}>
            <Calendar
              onChange={setDate}
              value={date}
              minDate={new Date()}
              tileDisabled={({ date }) => isDateDisabled(date)}
            />
          </DrawerBody>

          <DrawerFooter boxShadow="0px 5px 10px rgba(0, 0, 0, 0.4)">
            <Button
              bg={"#FF8E3C"}
              _hover={{ bg: "#FF8E3C" }}
              borderRadius={"30px"}
              color={"white"}
              w="100%"
              h="58px"
              fontSize={"17px"}
              fontFamily={"RedHatDisplay-Bold"}
              onClick={dateDisclosure.onClose}
            >
              Select Date
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <PaymentDisclosure
        paymentDisclosure={paymentDisclosure}
        loading={loading}
        handlePayPress={handlePayPress}
        CardElement={CardElement}
      />
    </Box>
  );
};
